import { AnimationController } from "./animation.js";
import { createPathPoints } from "./tmp.js";
import {
  extractLoop,
  removeLoop,
  resampleRiver,
  riverInit,
  setTerrain,
  slopeVector,
} from "./draw.js";
import {
  calculateCurvature,
  computeBitangents,
  computeTangents,
  constrainVector,
  drawVectors,
  drawVectorsCentered,
  findIntersection,
} from "./utils.js";
import { WorldCanvas } from "./coordinates.js";
import TerrainGenerator from "./terrain.js";
import alea from "alea";

const initTime = Date.now();
let endInitTime;

const canvas = document.getElementById("canvas");
const ctx = canvas.getContext("2d");

let river;
let bounds;
let slopeVectors = [];
const SEED = Math.round(Math.random() * 1e6);
// const SEED = 762;
console.log("seed", SEED);
const PRINT_ANIMATION_FRAME = false;
const DRAW_CHANNELS = true;
const DRAW_CONTOURS = true;
const DRAW_CUTOFFS = true;
const DRAW_EROSION = false;
const DRAW_HIST = true;
const DRAW_INTERSECTIONS = false;
const DRAW_NOISE = false;
const DRAW_RIVER = true;
const DRAW_SLOPE = false;
const DRAW_VECTORS = false;
const SHORTEN_CUTOFFS = false;
const SKIP_N_FRAMES = 0;
const SHOW_FPS = false;
const W = 30;
const TANGENT_INFLUENCE = 0.3;
const BITANGENT_INFLUENCE = 1;
const SLOPE_INFLUENCE = 3;
const MAX_VECTOR_LENGTH = 2;
const SAVE_HIST_EVERY = 20;
// window.stopAtAnimationFrame = 100;

const COLORS = {
  BACKGROUND: [247, 251, 232],
  ACTIVE_CHANNEL: [110, 157, 223],
  INACTIVE_CHANNEL: [245, 222, 179],
  FADE_COLOR: [255, 255, 255],
  OUTLINE: [14, 68, 114],
  BLACK: [0, 0, 0],
  WHITE: [255, 255, 255],
  CONTOUR: [0, 0, 0, 0.1],
};

function rgb(c) {
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

const CONTOUR_INTERVAL = 0.03; // Interval between contour lines (0-1)

const prng = alea(SEED);
let cachedNoiseImage = null;
let cachedContours = null;
let lastNoiseWidth = 0;
let lastNoiseHeight = 0;

let worldCanvas;
let terrain;

function computeBounds(x, y) {
  const minX = Math.min(...x);
  const maxX = Math.max(...x);
  return {
    minX,
    maxX,
    minY: Math.min(...y),
    maxY: Math.max(...y),
    width: maxX - minX,
    height: Math.max(...y) - Math.min(...y),
  };
}

function blendVectors(river, i, tangent, bitangent) {
  const x = river.x[i];
  const y = river.y[i];
  const g = river.g[i];
  const w = river.w[i];
  // Calculate normalized position (0 to 1) relative to screen width
  const screenX = (x - bounds.minX) / bounds.width;
  const fadePaddingPercent = 0.08;

  // Calculate edge influence factor (0 near edges, 1 in center)
  let edgeFactor = Math.min(
    1,
    Math.min(screenX / fadePaddingPercent, 1),
    Math.min((1 - screenX + 30) / fadePaddingPercent, 1),
  );
  if (screenX >= 1) {
    edgeFactor = 0;
  }

  const slope = slopeVector(x, y, w);
  if (DRAW_SLOPE) {
    slopeVectors.push({
      slope: [slope[0] * SLOPE_INFLUENCE, slope[1] * SLOPE_INFLUENCE],
      x,
      y,
    });
  }

  const G_FACTOR = 1 - g;

  const tx =
    tangent[0] * TANGENT_INFLUENCE * edgeFactor * terrain.p(10) * G_FACTOR +
    bitangent[0] * BITANGENT_INFLUENCE * edgeFactor * terrain.p(10) * G_FACTOR +
    slope[0] * SLOPE_INFLUENCE * edgeFactor;
  const ty =
    tangent[1] * TANGENT_INFLUENCE * edgeFactor * terrain.p(10) * G_FACTOR +
    bitangent[1] * BITANGENT_INFLUENCE * edgeFactor * terrain.p(10) * G_FACTOR +
    slope[1] * SLOPE_INFLUENCE * edgeFactor;

  const unconstrained = [tx, ty];
  return constrainVector(unconstrained, MAX_VECTOR_LENGTH);
}

function generateRiver() {
  // Initialize terrain before river
  terrain = new TerrainGenerator(prng);
  setTerrain(terrain);
  river = riverInit(W);
  bounds = computeBounds(river.x, river.y);
  window.river = river;
  window.bounds = bounds;

  worldCanvas = new WorldCanvas(canvas, bounds);

  // Store initial endpoints
  river.startPoint = { x: river.x[0], y: river.y[0] };
  river.endPoint = {
    x: river.x[river.x.length - 1],
    y: river.y[river.y.length - 1],
  };
}

function updateRiver(blend = true) {
  if (blend) {
    const tangents = computeTangents(river.x, river.y, MAX_VECTOR_LENGTH);
    river.curvature = calculateCurvature(river.x, river.y);
    const bitangents = computeBitangents(
      tangents,
      river.curvature,
      MAX_VECTOR_LENGTH,
    );

    slopeVectors = [];
    for (let i = 0; i < river.x.length; i++) {
      const v = blendVectors(river, i, tangents[i], bitangents[i]);
      river.x[i] -= v[0];
      river.y[i] -= v[1];
    }
  }
  resampleRiver(river);
}

window.n = { min: Infinity, max: -Infinity };

function computeNoiseField() {
  const { width, height } = canvas;
  if (
    width === lastNoiseWidth &&
    height === lastNoiseHeight &&
    (!DRAW_NOISE || cachedNoiseImage)
  ) {
    return;
  }

  const noiseValues = new Array(height);

  console.time("computeNoiseField - terrain");
  for (let x = Math.floor(bounds.minX); x < Math.ceil(bounds.maxX); x++) {
    for (let y = Math.floor(bounds.minY); y < Math.ceil(bounds.maxY); y++) {
      terrain.getElevation(x, y);
    }
  }
  console.timeEnd("computeNoiseField - terrain");
  console.time("computeNoiseField - noise");
  // Store noise values
  for (let y = 0; y < height; y++) {
    noiseValues[y] = new Array(width);
    for (let x = 0; x < width; x++) {
      const worldPos = worldCanvas.toWorld(x, y);
      let finalNoise = terrain.getElevation(worldPos.x, worldPos.y);

      noiseValues[y][x] = finalNoise;
    }
  }
  console.timeEnd("computeNoiseField - noise");

  // Create debug visualization if needed
  if (DRAW_NOISE) {
    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = (y * width + x) * 4;
        const elevation = noiseValues[y][x];
        if (elevation > 0.2) {
          data[index] = 255;
          data[index + 1] = 0;
          data[index + 2] = 0;
        } else if (elevation > -0.01 && elevation < 0.01) {
          data[index] = 0;
          data[index + 1] = 255;
          data[index + 2] = 0;
        } else {
          // min = -3
          // max = 0.2
          const normalized = (elevation + 3) / 3.2;
          const quantized = Math.round(normalized * 20) / 20;
          const color = quantized * 255;
          data[index] = color;
          data[index + 1] = color;
          data[index + 2] = color;
        }
        data[index + 3] = 255;
      }
    }
    cachedNoiseImage = imageData;
  }

  console.time("computeNoiseField - contours");
  // Compute contours
  computeContours(noiseValues, width, height);
  console.timeEnd("computeNoiseField - contours");

  lastNoiseWidth = width;
  lastNoiseHeight = height;
}
function drawErosion(ctx) {
  if (!DRAW_EROSION) return;
  const { width, height } = canvas;
  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const worldPos = worldCanvas.toWorld(x, y);
      const erosion = terrain.getErosion(worldPos.x, worldPos.y);
      const aggradation = terrain.getAggradation(worldPos.x, worldPos.y);

      const index = (y * width + x) * 4;
      if (aggradation + erosion < 5) {
        data[index] = 247;
        data[index + 1] = 251;
        data[index + 2] = 232;
        data[index + 3] = 255;
      } else if (aggradation > erosion) {
        const n = Math.min(255, (aggradation / 10) * 255);
        data[index] = n;
        data[index + 1] = n;
        data[index + 2] = n;
        data[index + 3] = n;
      } else {
        const n = 255 - Math.min((erosion / 10) * 255);
        data[index] = n;
        data[index + 1] = n;
        data[index + 2] = n;
        data[index + 3] = 255;
      }
    }
  }
  ctx.putImageData(imageData, 0, 0);
}
function drawDebugNoise(ctx) {
  if (!DRAW_NOISE) return;
  if (cachedNoiseImage) {
    ctx.putImageData(cachedNoiseImage, 0, 0);
  }
}
function computeContours(noiseValues, width, height) {
  cachedContours = [];
  for (let threshold = 0.2; threshold <= 1; threshold += CONTOUR_INTERVAL) {
    const contourPaths = [];

    for (let y = 0; y < height - 1; y++) {
      for (let x = 0; x < width - 1; x++) {
        const cell = [
          noiseValues[y][x],
          noiseValues[y][x + 1],
          noiseValues[y + 1][x + 1],
          noiseValues[y + 1][x],
        ];

        const segments = getContourSegments(x, y, cell, threshold);
        if (segments.length > 0) {
          contourPaths.push(...segments);
        }
      }
    }

    if (contourPaths.length > 0) {
      cachedContours.push({
        threshold,
        paths: contourPaths,
      });
    }
  }
}

function drawContours(ctx) {
  if (!DRAW_CONTOURS) return;
  if (!cachedContours) return;

  ctx.lineWidth = 1;
  for (const contour of cachedContours) {
    ctx.strokeStyle = interpolateColor(
      COLORS.WHITE,
      COLORS.BLACK,
      contour.threshold,
    );
    // if (contour.threshold > 0.75 && contour.threshold - 0.75 < 0.01) {
    //   ctx.strokeStyle = "red";
    //   ctx.lineWidth = 3;
    // }
    ctx.beginPath();
    for (const segment of contour.paths) {
      ctx.moveTo(segment.x1, segment.y1);
      ctx.lineTo(segment.x2, segment.y2);
    }
    ctx.stroke();
  }
}

function getContourSegments(x, y, cell, threshold) {
  let squareIndex = 0;
  if (cell[0] > threshold) squareIndex |= 1;
  if (cell[1] > threshold) squareIndex |= 2;
  if (cell[2] > threshold) squareIndex |= 4;
  if (cell[3] > threshold) squareIndex |= 8;

  const segments = [];

  // Linear interpolation between two values
  const interpolate = (v1, v2) => {
    if (v1 === v2) return 0.5;
    return (threshold - v1) / (v2 - v1);
  };

  // Helper to add a segment
  const addSegment = (x1, y1, x2, y2) => {
    segments.push({ x1, y1, x2, y2 });
  };

  // Interpolate edge points
  const left = interpolate(cell[0], cell[3]);
  const right = interpolate(cell[1], cell[2]);
  const top = interpolate(cell[0], cell[1]);
  const bottom = interpolate(cell[3], cell[2]);

  switch (squareIndex) {
    case 1:
    case 14:
      addSegment(x, y + left, x + top, y);
      break;
    case 2:
    case 13:
      addSegment(x + top, y, x + 1, y + right);
      break;
    case 3:
    case 12:
      addSegment(x, y + left, x + 1, y + right);
      break;
    case 4:
    case 11:
      addSegment(x + 1, y + right, x + bottom, y + 1);
      break;
    case 5:
      addSegment(x, y + left, x + top, y);
      addSegment(x + 1, y + right, x + bottom, y + 1);
      break;
    case 6:
    case 9:
      addSegment(x + top, y, x + bottom, y + 1);
      break;
    case 7:
    case 8:
      addSegment(x, y + left, x + bottom, y + 1);
      break;
    case 10:
      addSegment(x + top, y, x + 1, y + right);
      addSegment(x, y + left, x + bottom, y + 1);
      break;
    case 15:
    case 0:
      break;
  }

  return segments;
}

const animation = new AnimationController();

function prepareFrame(currentTime) {
  const frameState = animation.prepareFrame(currentTime);
  if (!frameState) return false;

  const { steps, adjustedTime } = frameState;

  if (steps > 0) {
    while (animation.frame < SKIP_N_FRAMES / MAX_VECTOR_LENGTH) {
      animation.frame++;
      updateRiver();
    }
    for (let s = 0; s < steps; s++) {
      updateRiver();
    }
    if (PRINT_ANIMATION_FRAME) console.log({ frame: animation.frame });
    animation.postUpdate(steps);
    if (animation.frame >= window.stopAtAnimationFrame) {
      animation.isPaused = true;
    }
  }

  ctx.fillStyle = rgb(COLORS.BACKGROUND);
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  computeNoiseField();
  drawDebugNoise(ctx);
  drawErosion(ctx);
  drawContours(ctx);

  if (DRAW_SLOPE) {
    slopeVectors.forEach(({ slope, x, y }) => {
      const vs = 100;
      const point = worldCanvas.toScreen(x, y);
      const vector = worldCanvas.vectorToScreen(slope[0], slope[1]);

      ctx.beginPath();
      ctx.strokeStyle = "#ff0000";
      ctx.lineWidth = 1;
      ctx.moveTo(point.x, point.y);
      ctx.lineTo(point.x + vector.x * vs, point.y + vector.y * vs);
      ctx.stroke();
    });
  }

  return { adjustedTime };
}

function drawIntersections(ctx, intersections, segment) {
  if (DRAW_INTERSECTIONS) {
    ctx.strokeStyle = "#ff0000";
    ctx.lineWidth = 2;
    const boxSize = 20;
    for (const point of worldCanvas.transformPoints(intersections)) {
      ctx.strokeRect(
        point.x - boxSize / 2,
        point.y - boxSize / 2,
        boxSize,
        boxSize,
      );
    }

    const startPoint = worldCanvas.toScreen(
      river.x[segment.start],
      river.y[segment.start],
    );
    const endPoint = worldCanvas.toScreen(
      river.x[segment.end],
      river.y[segment.end],
    );

    ctx.strokeStyle = "#00ff00";
    ctx.lineWidth = 2;
    ctx.strokeRect(
      startPoint.x - boxSize / 2,
      startPoint.y - boxSize / 2,
      boxSize,
      boxSize,
    );
    ctx.strokeStyle = "#0000ff";
    ctx.lineWidth = 2;
    ctx.strokeRect(
      endPoint.x - boxSize / 2,
      endPoint.y - boxSize / 2,
      boxSize,
      boxSize,
    );

    //   const loopPoints = extractLoop(channels, segment);

    //   // Only draw if we have a valid loop
    //   if (loopPoints && loopPoints.length > 2 && loopPoints.length < 500) {
    //     ctx.save();

    //     // Create clipping path for the loop
    //     ctx.beginPath();
    //     ctx.moveTo(loopPoints[0].x, loopPoints[0].y);
    //     for (const point of loopPoints) {
    //       ctx.lineTo(point.x, point.y);
    //     }
    //     ctx.closePath();

    //     // Only fill the actual loop area
    //     ctx.fillStyle = "rgba(0, 255, 0, 0.2)";
    //     ctx.fill();

    //     ctx.restore();
    //   }
  }
}

function drawRiver(currentTime) {
  const frameState = prepareFrame(currentTime);
  if (!frameState) return;

  const { adjustedTime } = frameState;

  let channels = createPathPoints(river, worldCanvas);

  // if (animation.frame % 50 === 49) {
  //   cachedContours = null;
  //   // cachedNoiseImage = null;
  //   // noiseValues = null;
  //   lastNoiseWidth++;
  // }
  if (animation.frame % SAVE_HIST_EVERY === 0 && DRAW_HIST) {
    river.hist.push({
      time: currentTime,
      channels: channels,
      type: "channel",
      coords: "world",
    });
    if (river.hist.length > 500) {
      river.hist.shift();
    }
  }

  for (let channel = 0; channel < 2; channel++) {
    let { intersections, intersectionSegments } = findIntersection(
      channels[channel],
      river.hist.filter((h) => h.type === "cutoff"),
    );

    intersectionSegments.forEach((segment) => {
      drawIntersections(ctx, intersections, segment);
    });

    intersectionSegments = intersectionSegments.filter(
      (s) => s.type === "self",
    );

    let segment;

    while ((segment = intersectionSegments.pop())) {
      if (segment.end - segment.start < 20) continue;
      const loopPoints = extractLoop(channels[channel], segment);
      const remove =
        loopPoints && loopPoints.length > 10 && loopPoints.length < 500;
      // if (!isPaused) console.log({ frame, remove, segment, loopPoints });

      if (remove) {
        river = removeLoop(river, segment, currentTime);

        updateRiver(false);

        channels = createPathPoints(river, worldCanvas);
        ({ intersections, intersectionSegments } = findIntersection(
          channels[channel],
        ));
        intersectionSegments = intersectionSegments.filter(
          (s) => s.type === "self",
        );
      }
    }
  }

  if (DRAW_HIST) {
    river.hist.forEach((hist, i) => {
      const factor = getFactor(hist.time, adjustedTime);
      if (factor < 0.01) return;
      if (DRAW_CUTOFFS && hist.type === "cutoff") {
        if (!hist._cachedPaths) {
          hist._cachedPaths = createPathPoints(hist, worldCanvas);
        }
        hist._cachedPaths.forEach((points) => {
          const fillColor = getColor(
            hist.time,
            adjustedTime,
            COLORS.INACTIVE_CHANNEL,
            COLORS.ACTIVE_CHANNEL,
          );
          const strokeColor = getColor(
            hist.time,
            adjustedTime,
            COLORS.OUTLINE,
            COLORS.FADE_COLOR,
          );
          renderChannel(ctx, points, fillColor, strokeColor);
          if (SHORTEN_CUTOFFS) {
            factor = getFactor(hist.time, adjustedTime) * 2;
            hist.x = getMiddlePortion(hist.x, factor);
            hist.y = getMiddlePortion(hist.y, factor);
            if (hist.x.length < 3) {
              river.hist.splice(i, 1);
            }
          }
        });
      } else if (DRAW_CHANNELS && hist.type === "channel") {
        hist.channels.forEach((channel) => {
          const fillColor = getColor(
            hist.time,
            adjustedTime,
            COLORS.ACTIVE_CHANNEL,
            COLORS.FADE_COLOR,
          );
          const strokeColor = getColor(
            hist.time,
            adjustedTime,
            COLORS.OUTLINE,
            COLORS.FADE_COLOR,
          );
          renderChannel(ctx, channel, fillColor, strokeColor);
        });
      }
    });
  }

  if (DRAW_RIVER) {
    channels.forEach((channel) => {
      renderChannel(
        ctx,
        channel,
        rgb(COLORS.ACTIVE_CHANNEL),
        rgb(COLORS.OUTLINE),
      );
    });
  }

  if (DRAW_VECTORS) {
    drawVectorsDebug(ctx);
  }

  if (SHOW_FPS) {
    ctx.font = "16px monospace";
    ctx.fillStyle = rgb(COLORS.BLACK);
    ctx.fillText(`FPS: ${animation.currentFps}`, 10, 20);
  }

  if (!endInitTime) {
    endInitTime = Date.now();
    console.log("init time", endInitTime - initTime);
  }
  animation.req = requestAnimationFrame(drawRiver);
}

function renderChannel(
  ctx,
  channel,
  fillColor,
  strokeColor,
  lineWidth = 1,
  close = false,
) {
  if (channel.length === 0) return;
  if (Array.isArray(channel[0])) {
    channel.forEach((island) => {
      renderChannel(
        ctx,
        island,
        rgb(COLORS.BACKGROUND),
        rgb(COLORS.OUTLINE),
        0.5,
        true,
      );
    });
    return;
  }

  const sx = worldCanvas.startX;
  const sy = worldCanvas.startY;
  const sc = worldCanvas.scale;

  // Fill — inline transform, no intermediate array
  ctx.beginPath();
  ctx.fillStyle = fillColor;
  ctx.moveTo(sx + channel[0].x * sc, sy + channel[0].y * sc);
  for (let i = 1; i < channel.length; i++) {
    ctx.lineTo(sx + channel[i].x * sc, sy + channel[i].y * sc);
  }
  ctx.closePath();
  ctx.fill();

  // Stroke — segmented by gap, inline transform
  ctx.beginPath();
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = lineWidth;
  let segStarted = false;
  for (let i = 0; i < channel.length; i++) {
    const g = channel[i].gap;
    const px = sx + channel[i].x * sc;
    const py = sy + channel[i].y * sc;
    if (g === undefined) {
      if (!segStarted) {
        ctx.moveTo(px, py);
      } else {
        ctx.lineTo(px, py);
      }
      if (close) ctx.closePath();
      segStarted = false;
    } else if (g > 0) {
      if (!segStarted) {
        ctx.moveTo(px, py);
        segStarted = true;
      } else {
        ctx.lineTo(px, py);
      }
    } else {
      if (segStarted && close) ctx.closePath();
      segStarted = false;
    }
  }
  if (segStarted && close) ctx.closePath();
  ctx.stroke();
}

const interpolateColor = (c1, c2, factor) => {
  const r = Math.round(c1[0] * (1 - factor) + c2[0] * factor);
  const g = Math.round(c1[1] * (1 - factor) + c2[1] * factor);
  const b = Math.round(c1[2] * (1 - factor) + c2[2] * factor);
  return `rgb(${r}, ${g}, ${b})`;
};

const getFactor = (time, currentTime) =>
  Math.min(1, Math.max(0, time / currentTime));

const getColor = (time, currentTime, baseColor, fadeColor) => {
  const factor = getFactor(time, currentTime);
  return interpolateColor(fadeColor, baseColor, factor);
};

function drawVectorsDebug(ctx) {
  const tangents = computeTangents(river.x, river.y);
  const curvature = calculateCurvature(river.x, river.y);
  const bitangents = computeBitangents(tangents, curvature, MAX_VECTOR_LENGTH);
  const blendedVectors = river.x.map((x, i) =>
    blendVectors(river, i, tangents[i], bitangents[i]),
  );

  drawVectors(
    ctx,
    river.x.map((x) => worldCanvas.toScreen(x, 0).x),
    river.y.map((y) => worldCanvas.toScreen(0, y).y),
    blendedVectors,
    "#0000ff",
  );
  drawVectorsCentered(
    ctx,
    river.x.map((x) => worldCanvas.toScreen(x, 0).x),
    river.y.map((y) => worldCanvas.toScreen(0, y).y),
    tangents,
    "#ff0000",
  );
}

function getMiddlePortion(xs, percentage) {
  if (!Array.isArray(xs) || xs.length === 0) {
    return [];
  }
  if (percentage > 1) return xs;

  if (percentage <= 0 || percentage > 1) {
    throw new Error("Percentage must be between 0 and 1");
  }

  const totalLength = xs.length;
  const portionLength = Math.round(totalLength * percentage);
  if (totalLength - portionLength < 2) return xs;
  const startIndex = Math.round((totalLength - portionLength) / 2);
  const endIndex = startIndex + portionLength;
  console.log({ factor, startIndex, endIndex });

  return xs.slice(startIndex, endIndex);
}

function resetAnimationState() {
  animation.reset();
  cachedNoiseImage = null;
  cachedContours = null;
  lastNoiseWidth = 0;
  lastNoiseHeight = 0;
}

// Add debounced resize handler
let resizeTimeout;
function handleResize(immediate = false) {
  const resize = () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    bounds = computeBounds(river.x, river.y);
    worldCanvas.updateBounds(bounds);
    resetAnimationState();
    drawRiver(performance.now());
  };

  if (immediate) {
    resize();
  } else {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    resizeTimeout = setTimeout(resize, 250); // 250ms debounce
  }
}

// Initial setup
generateRiver();
handleResize(true); // Pass true for immediate resize
animation.req = requestAnimationFrame(drawRiver);
window.addEventListener("resize", () => handleResize(false));
